import { type RefObject, useEffect } from 'react';

/**
 * Closes a floating thing when the user presses anywhere else.
 *
 * ## Why it is shared rather than written per menu
 *
 * The context menu had this logic and nothing else did, so the export dropdown and the hints
 * panel stayed open when you clicked the drawing — you had to go back and press the button that
 * opened them. A menu that survives a click on the canvas reads as stuck, and the canvas is the
 * one place a user is certain to click next.
 *
 * The three subtleties below are the reason this is one function instead of three copies. Each
 * was learned once and would have to be learned again by anyone writing the fourth menu.
 *
 * ## `pointerdown`, in the capture phase
 *
 * Not `click`. Without the capture phase the press that dismisses the menu also reaches the
 * canvas underneath, so opening the export menu, thinking better of it, and clicking away
 * leaves a stray point where you clicked — a bug a user cannot explain afterwards and would
 * not think to report. The press is swallowed: dismissing is what that press was *for*.
 *
 * ## Escape, and losing the window
 *
 * A menu open behind an alt-tab is a menu the user will come back to and not remember opening.
 * Both are the same event as far as the menu is concerned: it is no longer the thing being
 * attended to.
 *
 * @param ref   The floating element. A press inside it is not a dismissal.
 * @param onClose Called for a press outside, Escape, or the window losing focus.
 * @param active Whether to listen at all. Passing `false` is how a caller says "closed already".
 */
export function useDismissOnOutside(
  ref: RefObject<HTMLElement | null>,
  onClose: () => void,
  active = true
): void {
  useEffect(() => {
    if (!active) return;

    const away = (event: PointerEvent): void => {
      if (ref.current?.contains(event.target as Node) === true) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('pointerdown', away, true);
    window.addEventListener('keydown', key);
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('pointerdown', away, true);
      window.removeEventListener('keydown', key);
      window.removeEventListener('blur', onClose);
    };
  }, [ref, onClose, active]);
}
